/**
 * Alta y cambio de un elemento del catálogo de precios (6/10/2026): un
 * bloque principal, un médico, un apartado, un tratamiento, una cirugía, un
 * hospital, un forfait, un grupo de servicios o un servicio. Los campos de
 * cada tipo están en `lib/catalogoPrecios.CAMPOS_FORMULARIO`.
 *
 * Los importes se escriben como se dicen («1.234 €», «~120 € + IVA»,
 * «99 €/noche»; ejemplos inventados): los lee el servidor, con la misma regla
 * que al importar, y si no los entiende lo dice y la ventana sigue abierta.
 *
 * Desde el 28/10/2026 (administración):
 *  - Un tratamiento y una cirugía llevan precio + desplegable «Con IVA» /
 *    «Sin IVA» + anestesia + duración (min, opcional) + nota aparte, igual
 *    en los dos.
 *  - Con `destino.multiple` (los botones de arriba «Añadir cirugía» /
 *    «Añadir tratamiento»), la misma partida se crea en cada médico o
 *    bloque marcado (`POST /api/catalogo-precios/multiple`): una fila por
 *    sitio, luego cada una con su nota y su tiempo.
 *  - Al editar una partida, «Dónde» la mueve (a un apartado de la lista
 *    anterior, a un bloque o a un médico) y «Añadir a otro médico» la copia
 *    con todos sus campos (`POST /{id}/copiar`), sin crearla de nuevo.
 *  - Un bloque o un médico puede enlazarse con una ficha de `surgeons`
 *    (`destino.fichas`, el desplegable); los nombres vienen de la base.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Copy, Loader2, Save } from 'lucide-react';
import { api, mensajeError } from '../../lib/api';
import {
  CAMPOS_FORMULARIO, NOMBRE_TIPO, PARTIDAS, cuerpoFormulario, valoresFormulario,
} from '../../lib/catalogoPrecios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';

const SELECT = 'w-full h-10 px-3 rounded-md border border-input bg-background text-sm';

/** Las casillas de los médicos y bloques a los que va una partida. */
function Destinos({ destinos, marcados, onToggle, testidPrefix, titulo, ayuda }) {
  return (
    <fieldset className="rounded-md border border-border p-3" data-testid={`${testidPrefix}-destinos`}>
      <legend className="px-1 text-sm font-medium text-[#13231E]">{titulo}</legend>
      {ayuda && <p className="text-xs text-muted-foreground mb-2">{ayuda}</p>}
      {destinos.length === 0 ? (
        <p className="text-xs text-muted-foreground">No hay otros médicos ni bloques todavía.</p>
      ) : (
        <div className="grid gap-1.5 sm:grid-cols-2">
          {destinos.map((d) => (
            <label key={d.id} className="flex items-start gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#2E6E5E]" checked={marcados.includes(d.id)}
                onChange={() => onToggle(d.id)} data-testid={`${testidPrefix}-destino-${d.id}`} />
              <span className={d.tipo === 'bloque' ? 'font-medium' : ''}>{d.etiqueta}</span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

/**
 * @param destino  {tipo, padreId, seccion, elemento?, apartados?, destinos?,
 *                 fichas?, multiple?, donde?}: qué se da de alta y dónde, o
 *                 qué se edita (`elemento`). `apartados` ([{id, etiqueta}]) y
 *                 `destinos` ([{id, etiqueta, tipo}]) son los sitios a los
 *                 que se mueve o se copia un tratamiento o una cirugía;
 *                 `fichas` ([{id, name}]) las fichas de médico para un
 *                 bloque o un médico; `multiple`, el alta en varios sitios.
 */
export default function ElementoPrecioDialog({ destino, onClose, onGuardado }) {
  const tipo = destino?.tipo;
  const elemento = destino?.elemento || null;
  const [form, setForm] = useState({});
  const [padre, setPadre] = useState('');
  const [marcados, setMarcados] = useState([]);
  const [copiarA, setCopiarA] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [copiando, setCopiando] = useState(false);

  useEffect(() => {
    if (!destino) return;
    setForm(valoresFormulario(destino.tipo, destino.elemento));
    setPadre(destino.elemento ? (destino.elemento.padre_id || '') : (destino.padreId || ''));
    setMarcados([]);
    setCopiarA([]);
  }, [destino]);

  if (!destino) return null;
  const campos = CAMPOS_FORMULARIO[tipo] || [];
  const nombreTipo = NOMBRE_TIPO[tipo] || tipo;
  // «Nueva cirugía», «Nuevo forfait»: la cirugía es la única femenina.
  const femenina = tipo === 'cirugia';
  const esPartida = PARTIDAS.includes(tipo);
  const fichas = Array.isArray(destino.fichas) ? destino.fichas : [];
  const apartados = Array.isArray(destino.apartados) ? destino.apartados : [];
  const destinos = Array.isArray(destino.destinos) ? destino.destinos : [];
  const multiple = !elemento && !!destino.multiple && esPartida;
  const puedeMover = !!elemento && esPartida && (apartados.length > 0 || destinos.length > 0);
  const otrosSitios = elemento && esPartida ? destinos.filter((d) => d.id !== (elemento.padre_id || '')) : [];
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const alternar = (lista, poner) => (id) => poner(lista.includes(id) ? lista.filter((x) => x !== id) : [...lista, id]);

  // Elegir una ficha en un médico rellena el nombre si está vacío (o si era
  // el de otra ficha): el nombre oficial viene de la base, no se teclea.
  const elegirFicha = (id) => {
    const ficha = fichas.find((f) => f.id === id);
    setForm((f) => {
      const eraDeFicha = fichas.some((x) => x.name === f.nombre);
      const nombre = tipo === 'medico' && ficha && (!f.nombre || eraDeFicha) ? ficha.name : f.nombre;
      return { ...f, medico_id: id, nombre };
    });
  };

  const enviar = async (e) => {
    e.preventDefault();
    const cuerpo = cuerpoFormulario(tipo, form);
    if (multiple && marcados.length === 0) {
      toast.error('Marca al menos un médico o un bloque.');
      return;
    }
    setGuardando(true);
    try {
      if (elemento) {
        await api.put(`/api/catalogo-precios/${elemento.id}`, {
          ...cuerpo, ...(puedeMover ? { padre_id: padre || null } : {}),
        });
      } else if (multiple) {
        await api.post('/api/catalogo-precios/multiple', { tipo, padres_ids: marcados, ...cuerpo });
      } else {
        await api.post('/api/catalogo-precios', {
          tipo, padre_id: destino.padreId || null,
          ...(destino.seccion ? { seccion: destino.seccion } : {}),
          ...cuerpo,
        });
      }
      const n = marcados.length;
      toast.success(elemento ? 'Guardado'
        : multiple && n > 1 ? `${nombreTipo.charAt(0).toUpperCase()}${nombreTipo.slice(1)} ${femenina ? 'añadida' : 'añadido'} en ${n} sitios`
          : `${nombreTipo.charAt(0).toUpperCase()}${nombreTipo.slice(1)} ${femenina ? 'añadida' : 'añadido'}`);
      onGuardado && onGuardado();
      onClose && onClose();
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido guardar'));
    } finally {
      setGuardando(false);
    }
  };

  const copiar = async () => {
    if (copiarA.length === 0) {
      toast.error('Marca a qué médico o bloque se añade.');
      return;
    }
    setCopiando(true);
    try {
      await api.post(`/api/catalogo-precios/${elemento.id}/copiar`, { padres_ids: copiarA });
      toast.success(`${femenina ? 'Añadida' : 'Añadido'} también en ${copiarA.length === 1 ? '1 sitio' : `${copiarA.length} sitios`}`);
      onGuardado && onGuardado();
      onClose && onClose();
    } catch (err) {
      toast.error(mensajeError(err, 'No se ha podido añadir a otro médico'));
    } finally {
      setCopiando(false);
    }
  };

  const titulo = elemento ? `Editar ${nombreTipo}`
    : multiple ? `${femenina ? 'Nueva' : 'Nuevo'} ${nombreTipo} en uno o varios médicos`
      : `${femenina ? 'Nueva' : 'Nuevo'} ${nombreTipo}`;

  return (
    <Dialog open onOpenChange={(o) => { if (!o && onClose) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
        </DialogHeader>
        <form onSubmit={enviar} className="space-y-3" data-testid="precio-formulario">
          {destino.donde && !multiple && <p className="text-xs text-muted-foreground">En: {destino.donde}</p>}
          {campos.map(([campo, etiqueta, clase]) => (
            <div key={campo}>
              {clase !== 'si_no' && <Label htmlFor={`precio-${campo}`}>{etiqueta}</Label>}
              {clase === 'largo' ? (
                <Textarea id={`precio-${campo}`} rows={campo === 'reglas' || campo === 'nota_final' ? 7 : 3}
                  value={form[campo] ?? ''} onChange={(e) => set(campo, e.target.value)}
                  data-testid={`precio-campo-${campo}`} />
              ) : clase === 'iva' ? (
                <select id={`precio-${campo}`} value={form[campo] ?? 'con'} onChange={(e) => set(campo, e.target.value)}
                  className={SELECT} data-testid={`precio-campo-${campo}`}>
                  <option value="con">Con IVA (el precio lo lleva)</option>
                  <option value="sin">Sin IVA (el precio va sin IVA)</option>
                </select>
              ) : clase === 'si_no' ? (
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input id={`precio-${campo}`} type="checkbox" className="mt-0.5 h-4 w-4 accent-[#2E6E5E]"
                    checked={!!form[campo]} onChange={(e) => set(campo, e.target.checked)}
                    data-testid={`precio-campo-${campo}`} />
                  <span>{etiqueta}</span>
                </label>
              ) : clase === 'ficha' ? (
                <select id={`precio-${campo}`} value={form[campo] ?? ''} onChange={(e) => elegirFicha(e.target.value)}
                  className={SELECT} data-testid={`precio-campo-${campo}`}>
                  <option value="">Sin ficha (nombre libre)</option>
                  {fichas.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                  {form[campo] && !fichas.some((f) => f.id === form[campo]) && (
                    <option value={form[campo]}>(ficha que ya no está)</option>
                  )}
                </select>
              ) : (
                <Input id={`precio-${campo}`} value={form[campo] ?? ''}
                  onChange={(e) => set(campo, e.target.value)}
                  required={campo === 'nombre'}
                  placeholder={clase === 'importe' ? '1.234 €' : campo === 'duracion' ? '90' : undefined}
                  inputMode={clase === 'importe' || campo === 'duracion' ? 'decimal' : undefined}
                  data-testid={`precio-campo-${campo}`} />
              )}
            </div>
          ))}
          {multiple && (
            <Destinos destinos={destinos} marcados={marcados} onToggle={alternar(marcados, setMarcados)}
              testidPrefix="precio-multiple" titulo="A qué médico(s) o bloque(s) aplica"
              ayuda="Aparece dentro de cada uno marcado; luego, en cada médico, se personalizan su nota y su duración." />
          )}
          {puedeMover && (
            <div>
              <Label htmlFor="precio-apartado">Dónde</Label>
              <select id="precio-apartado" value={padre} onChange={(e) => setPadre(e.target.value)}
                className={SELECT} data-testid="precio-campo-apartado">
                <option value="">Sin apartado (lista anterior)</option>
                {apartados.length > 0 && (
                  <optgroup label="Apartados de la lista anterior">
                    {apartados.map((a) => <option key={a.id} value={a.id}>{a.etiqueta}</option>)}
                  </optgroup>
                )}
                {destinos.length > 0 && (
                  <optgroup label="Bloques y médicos">
                    {destinos.map((d) => <option key={d.id} value={d.id}>{d.etiqueta}</option>)}
                  </optgroup>
                )}
              </select>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando || copiando} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="precio-guardar">
              {guardando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              {elemento ? 'Guardar' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
        {elemento && esPartida && (
          <div className="border-t border-border pt-3 space-y-2" data-testid="precio-copiar">
            <Destinos destinos={otrosSitios} marcados={copiarA} onToggle={alternar(copiarA, setCopiarA)}
              testidPrefix="precio-copiar" titulo="Añadir a otro médico"
              ayuda={`${femenina ? 'La misma cirugía' : 'El mismo tratamiento'}, con su precio, IVA, anestesia, duración y nota, también en los marcados (sin crearl${femenina ? 'a' : 'o'} de nuevo). Lo de aquí arriba no se guarda con este botón.`} />
            {otrosSitios.length > 0 && (
              <div className="flex justify-end">
                <Button type="button" variant="outline" onClick={copiar} disabled={copiando || guardando}
                  data-testid="precio-copiar-guardar">
                  {copiando ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Copy className="w-4 h-4 mr-1.5" />}
                  Añadir a los marcados
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
